import React from "react";
import { formatDateTime } from "@/lib/utils";
import { toast } from "sonner";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { KNOWLEDGE_BASE } from "@/constants/testIds";
import {
	useDocumentDetail,
	statusToLabel,
	statusToTone,
	jobStatusToLabel,
	formatBytes,
	deleteDocument,
	parseDeleteError,
	deleteErrorCopyFor,
	retryDocument,
	parseRetryError,
	retryErrorCopyFor,
} from "@/lib/knowledgeBase";

/**
 * Phase 4F — Slice 2: On-demand document detail Sheet.
 *
 * The Sheet performs exactly one GET
 *   /api/workspaces/{workspaceId}/documents/{documentId}
 * request per opening, aborts prior requests on close / unmount / switch,
 * and guards against stale-response overwrite via the generation counter
 * in useDocumentDetail. It never displays content_sha256, storage paths,
 * signed URLs, service credentials, stack traces, raw axios error text,
 * or raw backend payloads. It never renders a Retry / mutation button.
 */

function formatDate(iso) {
	if (typeof iso !== "string" || iso.length === 0) return "—";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "—";
	return formatDateTime(d);
}

function Field({ label, value, testId, mono = false }) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</span>
			<span
				className={
					"text-sm text-[#0B1F3A] " + (mono ? "font-mono break-all" : "")
				}
				style={{ unicodeBidi: "plaintext" }}
				data-testid={testId}
			>
				{value == null || value === "" ? "—" : value}
			</span>
		</div>
	);
}

function LoadingBody() {
	return (
		<div data-testid={KNOWLEDGE_BASE.detailLoading} className="space-y-3">
			<div className="h-4 bg-neutral-100 rounded animate-pulse" />
			<div className="h-4 bg-neutral-100 rounded animate-pulse w-3/4" />
			<div className="h-4 bg-neutral-100 rounded animate-pulse w-2/3" />
			<div className="h-4 bg-neutral-100 rounded animate-pulse w-1/2" />
		</div>
	);
}

function ErrorBody({ testId, title, body }) {
	return (
		<div
			data-testid={testId}
			className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
			role="alert"
		>
			<div className="font-medium">{title}</div>
			<div className="mt-1 text-red-700">{body}</div>
		</div>
	);
}

function ReadyBody({ detail }) {
	if (!detail || typeof detail !== "object") return null;
	const status = detail.status;
	const jobStatus = detail.latest_job_status;
	const errorCode = detail.error_code;
	const errorMessage = detail.error_message;
	return (
		<div data-testid={KNOWLEDGE_BASE.detailReady} className="space-y-4">
			<div className="grid grid-cols-1 gap-3">
				<Field
					label="Title"
					value={detail.title}
					testId={KNOWLEDGE_BASE.detailFieldTitle}
				/>
				<Field
					label="Original filename"
					value={detail.original_filename}
					testId={KNOWLEDGE_BASE.detailFieldFilename}
					mono
				/>
				<div className="grid grid-cols-2 gap-3">
					<Field
						label="File type"
						value={detail.mime_type}
						testId={KNOWLEDGE_BASE.detailFieldMime}
					/>
					<Field
						label="Size"
						value={formatBytes(detail.size_bytes)}
						testId={KNOWLEDGE_BASE.detailFieldSize}
					/>
				</div>
				<div className="flex flex-col gap-0.5">
					<span className="text-[10px] uppercase tracking-wide text-neutral-500">Status</span>
					<span>
						<span
							className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${statusToTone(status)}`}
							data-testid={KNOWLEDGE_BASE.detailFieldStatus}
							data-status={typeof status === "string" ? status : "unknown"}
							aria-label={`Status: ${statusToLabel(status)}`}
						>
							{statusToLabel(status)}
						</span>
					</span>
				</div>
				<Field
					label="Latest job status"
					value={jobStatusToLabel(jobStatus)}
					testId={KNOWLEDGE_BASE.detailFieldJobStatus}
				/>
				<div className="grid grid-cols-2 gap-3">
					<Field
						label="Created"
						value={formatDate(detail.created_at)}
						testId={KNOWLEDGE_BASE.detailFieldCreatedAt}
					/>
					<Field
						label="Updated"
						value={formatDate(detail.updated_at)}
						testId={KNOWLEDGE_BASE.detailFieldUpdatedAt}
					/>
				</div>
				{(errorCode || errorMessage) && (
					<div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
						<div className="text-[10px] uppercase tracking-wide text-red-700">
							Latest failure
						</div>
						{errorCode ? (
							<div
								className="text-xs font-mono text-red-900"
								data-testid={KNOWLEDGE_BASE.detailFieldErrorCode}
							>
								{errorCode}
							</div>
						) : null}
						{errorMessage ? (
							<div
								className="text-sm text-red-900"
								data-testid={KNOWLEDGE_BASE.detailFieldErrorMessage}
							>
								{errorMessage}
							</div>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
}

export default function KBDetailSheet({ workspaceId, documentId, isOpen, onOpenChange, onDeleted, onRetried }) {
	// The hook returns idle/null while documentId is null so we can safely
	// call it unconditionally.
	const { detailState, detail, errorInfo, refetchDetail } = useDocumentDetail(
		isOpen ? workspaceId : null,
		isOpen ? documentId : null
	);

	// Slice 4 — Document delete state. All destructive UX lives inside an
	// AlertDialog nested in the Sheet; the Sheet itself remains read-only.
	const [confirmOpen, setConfirmOpen] = React.useState(false);
	const [deleting, setDeleting] = React.useState(false);
	const [deleteError, setDeleteError] = React.useState(null);
	const mountedRef = React.useRef(true);
	const inFlightRef = React.useRef(false);

	// Slice 5B — Retry state. Retry is non-destructive so no confirm dialog.
	const [retrying, setRetrying] = React.useState(false);
	const [retryError, setRetryError] = React.useState(null);
	const retryInFlightRef = React.useRef(false);

	React.useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	// Reset delete-local + retry-local state whenever the Sheet transitions
	// to closed or the target document changes. Do NOT reset while any
	// request is in flight.
	React.useEffect(() => {
		if (inFlightRef.current || retryInFlightRef.current) return;
		setConfirmOpen(false);
		setDeleting(false);
		setDeleteError(null);
		setRetrying(false);
		setRetryError(null);
	}, [isOpen, documentId]);

	const canDelete =
		detailState === "ready" &&
		Boolean(workspaceId) &&
		Boolean(documentId) &&
		detail &&
		typeof detail === "object";

	// Slice 5B — Retry visibility. The backend is the sole authority for
	// eligibility; the frontend only surfaces the action for documents
	// whose loaded detail payload reports status="failed". Any refresh
	// that moves the document off "failed" hides the button and clears
	// any stale retry banner.
	const canRetry =
		detailState === "ready" &&
		Boolean(workspaceId) &&
		Boolean(documentId) &&
		detail &&
		typeof detail === "object" &&
		detail.status === "failed";

	// If the refreshed detail is no longer in `failed` state, clear any
	// obsolete retry banner. This runs after a 409-triggered refresh
	// resolves to a non-failed state (e.g. another actor already flipped
	// the document back to `uploaded`).
	React.useEffect(() => {
		if (retryInFlightRef.current) return;
		if (detailState !== "ready") return;
		if (!detail || detail.status !== "failed") {
			if (retryError !== null) setRetryError(null);
		}
	}, [detailState, detail, retryError]);

	const openConfirm = () => {
		if (inFlightRef.current) return;
		setDeleteError(null);
		setConfirmOpen(true);
	};

	const cancelConfirm = () => {
		if (inFlightRef.current) return;
		setConfirmOpen(false);
	};

	const doDelete = async () => {
		// Synchronous double-submit guard: must run BEFORE any async work.
		if (inFlightRef.current) return;
		if (!workspaceId || !documentId) return;

		inFlightRef.current = true;
		if (mountedRef.current) {
			setDeleting(true);
			setDeleteError(null);
		}

		try {
			await deleteDocument(workspaceId, documentId);
			if (!mountedRef.current) return;
			toast.success("Document deleted");
			// Reset local state, close the confirm dialog, then notify the
			// parent. onDeleted is called with the deleted document_id so the
			// parent can perform exactly one refetch and close the sheet.
			setConfirmOpen(false);
			setDeleteError(null);
			const deletedId = documentId;
			if (typeof onDeleted === "function") {
				try {
					const maybe = onDeleted(deletedId);
					if (maybe && typeof maybe.then === "function") {
						maybe.catch(() => {
							// Deletion already succeeded; a refresh failure is non-fatal.
						});
					}
				} catch (_syncThrow) {
					// Deletion already succeeded; a synchronous refresh failure is non-fatal.
				}
			}
		} catch (err) {
			if (!mountedRef.current) return;
			const parsed = parseDeleteError(err);
			if (parsed.kind === "silent" || parsed.kind === "canceled") return;
			setDeleteError(deleteErrorCopyFor(parsed));
		} finally {
			inFlightRef.current = false;
			if (mountedRef.current) setDeleting(false);
		}
	};

	// Radix onOpenChange for the AlertDialog: while deleting, ignore all
	// "close" attempts (Escape, overlay click).
	const handleConfirmOpenChange = (open) => {
		if (open) {
			setConfirmOpen(true);
			return;
		}
		if (inFlightRef.current) return;
		setConfirmOpen(false);
	};

	// Slice 5B — Retry handler. Non-destructive; no confirm dialog. On any
	// resolved outcome the parent list is refetched (via onRetried) AND the
	// currently open document detail is refetched (via refetchDetail) so the
	// UI reflects the freshest server truth. The RPC handles all eligibility
	// invariants; the frontend simply reacts to the response.
	const doRetry = async () => {
		// Synchronous double-submit guard: MUST run BEFORE any await.
		if (retryInFlightRef.current) return;
		if (!workspaceId || !documentId) return;

		retryInFlightRef.current = true;
		if (mountedRef.current) {
			setRetrying(true);
			setRetryError(null);
		}

		// Refresh both the workspace list and this document's detail after a
		// resolved retry attempt (success OR a non-catastrophic 409). Never
		// refresh for permission / validation / service / network errors —
		// those outcomes carry no new server-side state.
		const refreshBothForResolvedAttempt = () => {
			// Detail refresh — deterministic via the hook's refetchDetail().
			try {
				if (typeof refetchDetail === "function") refetchDetail();
			} catch (_syncThrow) {
				// A refresh trigger failure is non-fatal.
			}
			// List refresh — parent-owned callback. Tolerate both sync and
			// promise-returning implementations.
			if (typeof onRetried === "function") {
				try {
					const maybe = onRetried(documentId);
					if (maybe && typeof maybe.then === "function") {
						maybe.catch(() => {
							// A list refresh failure is non-fatal.
						});
					}
				} catch (_syncThrow) {
					// Sync throw is non-fatal.
				}
			}
		};

		try {
			await retryDocument(workspaceId, documentId);
			if (!mountedRef.current) return;
			toast.success("Retry queued");
			setRetryError(null);
			refreshBothForResolvedAttempt();
		} catch (err) {
			if (!mountedRef.current) return;
			const parsed = parseRetryError(err);
			if (parsed.kind === "canceled" || parsed.kind === "silent") return;
			setRetryError(retryErrorCopyFor(parsed));
			// Only refresh for the two backend-issued 409 hints, because those
			// are the only outcomes that reflect new server-side state.
			if (parsed.kind === "coded") {
				refreshBothForResolvedAttempt();
			}
		} finally {
			retryInFlightRef.current = false;
			if (mountedRef.current) setRetrying(false);
		}
	};

	return (
		<Sheet
			open={isOpen}
			onOpenChange={(open) => {
				// Do not allow the Sheet to close while a delete OR retry is in flight.
				if (!open && (inFlightRef.current || retryInFlightRef.current)) return;
				if (!open && typeof onOpenChange === "function") onOpenChange(false);
			}}
		>
			<SheetContent
				side="right"
				className="w-full sm:max-w-lg overflow-y-auto"
				data-testid={KNOWLEDGE_BASE.detailSheet}
			>
				<SheetHeader>
					<SheetTitle data-testid={KNOWLEDGE_BASE.detailSheetTitle}>
						Document details
					</SheetTitle>
					<SheetDescription data-testid={KNOWLEDGE_BASE.detailSheetDescription}>
						Read-only metadata for the selected Knowledge Base document.
					</SheetDescription>
				</SheetHeader>

				<div className="mt-6">
					{detailState === "loading" && <LoadingBody />}

					{detailState === "ready" && <ReadyBody detail={detail} />}

					{detailState === "permission_denied" && (
						<ErrorBody
							testId={KNOWLEDGE_BASE.detailPermissionError}
							title="Access denied"
							body="You do not have access to this document."
						/>
					)}

					{detailState === "not_found" && (
						<ErrorBody
							testId={KNOWLEDGE_BASE.detailNotFoundError}
							title="Document not found"
							body="This document is no longer available in your workspace."
						/>
					)}

					{detailState === "network_error" && (
						<ErrorBody
							testId={KNOWLEDGE_BASE.detailNetworkError}
							title="Network error"
							body="We couldn't reach the server. Please check your connection and try again."
						/>
					)}

					{detailState === "unexpected_error" && (
						<ErrorBody
							testId={KNOWLEDGE_BASE.detailUnexpectedError}
							title="Something went wrong"
							body="We couldn't load these details. Please try again."
						/>
					)}
				</div>

				{(canDelete || retryError) && (
					<div className="mt-6 pt-4 border-t border-black/5 flex flex-col gap-3">
						{retryError && (
							<div
								data-testid={KNOWLEDGE_BASE.detailRetryInlineMessage}
								role="alert"
								aria-live="assertive"
								className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800"
							>
								{retryError}
							</div>
						)}
						{canRetry && (
							<div className="flex justify-end">
								<Button
									type="button"
									variant="secondary"
									onClick={doRetry}
									disabled={retrying}
									aria-busy={retrying ? "true" : "false"}
									data-testid={KNOWLEDGE_BASE.detailRetryButton}
									aria-label={retrying ? "Retrying ingestion" : "Retry ingestion for this document"}
								>
									{retrying ? "Retrying…" : "Retry ingestion"}
								</Button>
							</div>
						)}
						{canDelete && (
							<div className="flex justify-end">
								<Button
									type="button"
									variant="destructive"
									onClick={openConfirm}
									disabled={deleting}
									data-testid={KNOWLEDGE_BASE.detailDeleteButton}
									aria-label="Delete this document"
								>
									Delete document
								</Button>
							</div>
						)}
					</div>
				)}

				<AlertDialog open={confirmOpen} onOpenChange={handleConfirmOpenChange}>
					<AlertDialogContent
						data-testid={KNOWLEDGE_BASE.detailDeleteConfirmDialog}
						aria-busy={deleting ? "true" : "false"}
						onEscapeKeyDown={(event) => {
							if (deleting) event.preventDefault();
						}}
						onPointerDownOutside={(event) => {
							if (deleting) event.preventDefault();
						}}
						onInteractOutside={(event) => {
							if (deleting) event.preventDefault();
						}}
					>
						<AlertDialogHeader>
							<AlertDialogTitle>Delete this document?</AlertDialogTitle>
							<AlertDialogDescription>
								This document will be removed from your Knowledge Base and can no
								longer be retrieved by the Knowledge Assistant. This action cannot
								be undone from this screen.
							</AlertDialogDescription>
						</AlertDialogHeader>
						{deleteError && (
							<div
								data-testid={KNOWLEDGE_BASE.detailDeleteInlineMessage}
								role="alert"
								aria-live="assertive"
								className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800"
							>
								{deleteError}
							</div>
						)}
						<AlertDialogFooter>
							<AlertDialogCancel
								onClick={(event) => {
									if (deleting) {
										event.preventDefault();
										return;
									}
									cancelConfirm();
								}}
								disabled={deleting}
								data-testid={KNOWLEDGE_BASE.detailDeleteCancelButton}
							>
								Cancel
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={(event) => {
									// Prevent Radix's default auto-close so we can drive the
									// close explicitly after the async delete resolves.
									event.preventDefault();
									doDelete();
								}}
								disabled={deleting}
								data-testid={KNOWLEDGE_BASE.detailDeleteConfirmButton}
								className="bg-red-600 text-white hover:bg-red-700"
							>
								{deleting ? "Deleting…" : "Delete"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				{/* errorInfo is intentionally NOT rendered here — the parsed public
				    error code/message from the backend is only rendered inside a
				    successful ReadyBody. Raw axios errors and raw backend payloads
				    are never surfaced to the user. */}
				<span data-error-info-code={errorInfo?.code || ""} style={{ display: "none" }} />
			</SheetContent>
		</Sheet>
	);
}
